import React from 'react';
import GenericDialogue from 'src/components/Dialogue/Dialogue';
import Table from 'src/components/Table';
import { ITableColumn } from 'src/components/Table/types';
import LoadState from 'src/components/LoadState/LoadState';
import Button from 'src/components/Button';
import Checkbox from 'src/components/Checkbox';
import Skeleton from 'src/components/Skeleton/Skeleton';
import LoadingButton from 'src/components/BulkOperations/_Shared/BulkActionButton/LoadingButton';
import CalcomBookingLinkPicker from 'src/components/CalcomBookingLinkPicker/CalcomBookingLinkPicker';
import KeyPickupSchedulingCallout from '../KeyPickupSchedulingCallout/KeyPickupSchedulingCallout';
import KeyPickupStatusBadge from '../KeyPickupStatusBadge/KeyPickupStatusBadge';
import { getDisabledCellStyle } from 'src/utils/CommonHelpers/commonhelpers';
import {
  IUnitKeyPickupStatus,
  IUserAssociatedProperty,
  JuniperEventType,
} from '../../types';
import { isKeyPickupUnitSelectable } from '../../CustomHooks/useKeyPickupInvite/useKeyPickupInvite';
import './key-pickup-invite-dialog.scss';

const SKELETON_ROW_COUNT: number = 6;

interface KeyPickupInviteFormProps {
  property: IUserAssociatedProperty;
  onClose: () => void;
  units: IUnitKeyPickupStatus[];
  isLoadingUnits: boolean;
  unitsError: string | null;
  selectedUnitIds: Set<string>;
  toggleUnit: (unitId: string) => void;
  selectAllEligible: () => void;
  deselectAll: () => void;
  eligibleCount: number;
  selectedBookingLinkId: string | null;
  selectBookingLink: (bookingLinkId: string | null) => void;
  isSubmitting: boolean;
  submitError: string | null;
  submit: () => Promise<void>;
}

const KeyPickupInviteForm = ({
  property,
  onClose,
  units,
  isLoadingUnits,
  unitsError,
  selectedUnitIds,
  toggleUnit,
  selectAllEligible,
  deselectAll,
  eligibleCount,
  selectedBookingLinkId,
  selectBookingLink,
  isSubmitting,
  submitError,
  submit,
}: KeyPickupInviteFormProps) => {
  const selectedCount: number = selectedUnitIds.size;

  const canSubmit: boolean =
    selectedCount > 0 && selectedBookingLinkId !== null && !isSubmitting;

  const allEligibleSelected: boolean =
    eligibleCount > 0 && selectedCount === eligibleCount;

  const onSelectAll = (): void => {
    if (allEligibleSelected) {
      deselectAll();
      return;
    }

    selectAllEligible();
  };

  const tableData: any[] = units.map((unit: IUnitKeyPickupStatus) => ({
    ...unit,
    id: unit.unitId,
  }));

  const columns: ITableColumn[] = [
    {
      label: (
        <Checkbox readOnly value={allEligibleSelected} onChange={onSelectAll} />
      ),
      width: '8rem',
      cell: (unit: IUnitKeyPickupStatus) => {
        const isSelectable: boolean = isKeyPickupUnitSelectable(unit);
        const isSelected: boolean = selectedUnitIds.has(unit.unitId);

        return (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              ...getDisabledCellStyle(!isSelectable),
            }}
          >
            <Checkbox
              checked={isSelected}
              name={unit.unitId}
              value={isSelected}
              onChange={() => toggleUnit(unit.unitId)}
              disabled={!isSelectable}
              readOnly
            />
          </div>
        );
      },
    },
    { label: 'Unit', accessor: 'number', width: '9rem' },
    {
      label: 'Homeowner',
      width: '25rem',
      cell: (unit: IUnitKeyPickupStatus) => unit.homeOwnerFullName || '—',
    },
    {
      label: 'Status',
      minWidth: '22rem',
      cell: (unit: IUnitKeyPickupStatus) => (
        <KeyPickupStatusBadge
          status={unit.keyPickupStatus}
          calcomBookingStatus={unit.calcomBookingStatus}
          calcomBookingUid={unit.calcomBookingUid}
          homeOwnerFullName={unit.homeOwnerFullName}
        />
      ),
    },
  ];

  const skeletonColumns: ITableColumn[] = columns.map(
    (column: ITableColumn) => ({
      ...column,
      cell: () => <Skeleton variant="text" width="70%" />,
    })
  );

  const skeletonData: any[] = Array.from(
    { length: SKELETON_ROW_COUNT },
    (_, index: number) => ({ id: `kp-unit-skeleton-${index}` })
  );

  return (
    <GenericDialogue
      isOpen
      onClose={onClose}
      title={`Invite key pickups — ${property.name}`}
      description="Pick a booking link, then the units whose homeowners should get a self-booking invite."
      maxWidth="130rem"
      minHeight="95vh"
      footer={
        <div className="key-pickup-invite-footer">
          <Button
            outlined
            size="small"
            label="Cancel"
            onClick={onClose}
            disabled={isSubmitting}
          />
          <LoadingButton
            actionText="Send invites"
            selectedCount={selectedCount}
            isSubmitting={isSubmitting}
            disabled={!canSubmit}
            onClick={() => submit()}
          />
        </div>
      }
    >
      <div className="key-pickup-invite-dialog">
        <KeyPickupSchedulingCallout />

        <CalcomBookingLinkPicker
          propertyId={property.propertyId}
          kind={JuniperEventType.KeyPickup}
          selectedValue={selectedBookingLinkId}
          onChange={selectBookingLink}
          required
        />

        <div className="units-section">
          <span className="units-summary">
            {eligibleCount} of {units.length} unit
            {units.length === 1 ? '' : 's'} available for invite
          </span>

          {isLoadingUnits ? (
            <Table
              columns={skeletonColumns}
              data={skeletonData}
              paginate={{ itemsPerPage: 8, position: 'bottom' }}
              paginationSkeleton
            />
          ) : (
            <LoadState
              isLoading={false}
              error={unitsError}
              isEmpty={units.length === 0}
              emptyNode={
                <p className="units-empty">No units found for this property.</p>
              }
            >
              <Table
                columns={columns}
                data={tableData}
                paginate={{ itemsPerPage: 8, position: 'bottom' }}
              />
            </LoadState>
          )}
        </div>

        {submitError && <p className="invite-error">{submitError}</p>}
      </div>
    </GenericDialogue>
  );
};

export default KeyPickupInviteForm;